import { createContext, useContext, useCallback, useRef } from "react";

import { getFileSignedUrl } from "@/api";

import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { ReactNode } from "react";

type ImageUrlContextType = (
  scope: UploadScope,
  key: string,
  forceRefresh?: boolean,
) => Promise<string | null>;

const ImageUrlContext = createContext<ImageUrlContextType | null>(null);

interface ImageUrlProviderProps {
  children: ReactNode;
}

export function ImageUrlProvider({ children }: ImageUrlProviderProps) {
  const imageUrlsMapRef = useRef<Map<string, string>>(new Map());

  const setImageUrl = useCallback((key: string, url: string) => {
    imageUrlsMapRef.current.set(key, url);
  }, []);

  const getImageUrl: ImageUrlContextType = useCallback(
    async (scope, key, forceRefresh = false) => {
      if (!forceRefresh && imageUrlsMapRef.current.has(key)) {
        return imageUrlsMapRef.current.get(key) || null;
      }

      try {
        const { signedUrl } = await getFileSignedUrl(scope.type, scope.uid, key);
        if (signedUrl) {
          setImageUrl(key, signedUrl);
          return signedUrl;
        }
      } catch {
        return null;
      }

      return null;
    },
    [setImageUrl],
  );

  return <ImageUrlContext.Provider value={getImageUrl}>{children}</ImageUrlContext.Provider>;
}

export function useGetImageUrl() {
  const context = useContext(ImageUrlContext);
  if (!context) {
    throw new Error("useGetImageUrl must be used within an ImageUrlProvider");
  }
  return context;
}
